import type { AuthSession } from './types';

const STORAGE_KEY = 'sicop.auth.session';

/**
 * A sessão fica em sessionStorage (morre ao fechar a aba) e sempre é revalidada
 * contra o `expiresAt` — a sessão do SICOP dura 15 minutos, renovada pelo uso.
 */
export function readSession(): AuthSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const session = JSON.parse(raw) as AuthSession;
    if (!session?.user?.email || !session.accessToken) return null;
    if (Date.now() >= session.expiresAt) {
      clearSession();
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

export function writeSession(session: AuthSession): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Sem storage disponível (aba anônima restrita): a sessão vive só em memória.
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignorado
  }
}

/**
 * A API recusou a sessão (401/403): o token venceu antes da hora, foi revogado,
 * ou a conta saiu da allowlist do servidor. Quem escuta é o `useGoogleAuth`,
 * que volta para a tela de login com a mensagem do servidor.
 */
export const SESSION_REJECTED_EVENT = 'sicop:session-rejected';

export function rejectSession(message: string): void {
  clearSession();
  window.dispatchEvent(new CustomEvent<string>(SESSION_REJECTED_EVENT, { detail: message }));
}

/**
 * O servidor renovou a sessão (toda resposta traz o prazo vigente no
 * cabeçalho `X-Sicop-Session-Expires-At`): o prazo novo entra no storage e o
 * `useGoogleAuth` — que escuta o evento — adia o logout automático.
 */
export const SESSION_RENEWED_EVENT = 'sicop:session-renewed';

export function renewSession(expiresAt: number): void {
  const session = readSession();

  if (!session || !Number.isFinite(expiresAt) || expiresAt <= session.expiresAt) return;

  writeSession({ ...session, expiresAt });
  window.dispatchEvent(new CustomEvent<number>(SESSION_RENEWED_EVENT, { detail: expiresAt }));
}
